import { ActivityShareType } from '@jms/model';
import type { Shareable } from '@jms/model';
import type { ResolvedShareOptions } from './share.options';

/** The record keys sharing is stored under; the capability bind replaces them in every save. */
export type ShareValue = Required<Pick<Shareable, 'systemSharing' | 'shared' | 'sensitive'>>;

export enum ShareToggle {
  System = 'system',
  Customer = 'customer',
  Field = 'field',
  Sensitive = 'sensitive',
}

export interface ShareMenuRow {
  readonly toggle: ShareToggle;
  readonly label: string;
  /** Draws the divider above this row. */
  readonly dividerBefore: boolean;
}

// A new record starts on automatic sharing, as the legacy creator did.
const SHARE_DEFAULTS: ShareValue = { systemSharing: true, shared: [], sensitive: false };

/** The server-resolved `effectiveSharing` wins; a record without one (create) falls back to its own keys, then the defaults. */
export function loadShareValue(record: Readonly<Record<string, unknown>>): ShareValue {

  const source = (record['effectiveSharing'] as Shareable | null | undefined) ?? topLevelShare(record);
  if (!source) return { ...SHARE_DEFAULTS, shared: [] };

  // Legacy sharing-pro reads a value without `systemSharing` as manual; keep that reading.
  return {
    systemSharing: source.systemSharing ?? false,
    shared: (source.shared ?? []).filter(type => type === ActivityShareType.Customer || type === ActivityShareType.Field),
    sensitive: source.sensitive ?? false,
  };
}

export function saveShareValue(value: Shareable | null | undefined): ShareValue {

  return {
    systemSharing: value?.systemSharing ?? SHARE_DEFAULTS.systemSharing,
    shared: [...(value?.shared ?? [])],
    sensitive: value?.sensitive ?? SHARE_DEFAULTS.sensitive,
  };
}

export function isShareToggleOn(value: ShareValue, toggle: ShareToggle): boolean {

  switch (toggle) {
    case ShareToggle.System: return value.systemSharing;
    case ShareToggle.Customer: return value.shared.includes(ActivityShareType.Customer);
    case ShareToggle.Field: return value.shared.includes(ActivityShareType.Field);
    case ShareToggle.Sensitive: return value.sensitive;
  }
}

/** Manual toggles are inert while system sharing decides; flipping system sharing either way clears them, as legacy did. */
export function applyShareToggle(value: ShareValue, toggle: ShareToggle, on: boolean): ShareValue {

  if (toggle === ShareToggle.System) return { systemSharing: on, shared: [], sensitive: false };
  if (value.systemSharing) return value;
  if (toggle === ShareToggle.Sensitive) return { ...value, sensitive: on };

  const type = toggle === ShareToggle.Customer ? ActivityShareType.Customer : ActivityShareType.Field;
  const others = value.shared.filter(shared => shared !== type);
  return { ...value, shared: on ? [...others, type] : others };
}

/** "Use system sharing" always; each manual toggle only when the parent offers it for this child. */
export function shareMenuRows(options: ResolvedShareOptions | null): readonly ShareMenuRow[] {

  return [
    { toggle: ShareToggle.System, label: 'Use system sharing', dividerBefore: false },
    ...(options?.customer ? [{ toggle: ShareToggle.Customer, label: 'Share with customer', dividerBefore: false }] : []),
    ...(options?.field ? [{ toggle: ShareToggle.Field, label: 'Share with field', dividerBefore: false }] : []),
    ...(options?.sensitive ? [{ toggle: ShareToggle.Sensitive, label: 'Mark sensitive', dividerBefore: true }] : []),
  ];
}

function topLevelShare(record: Readonly<Record<string, unknown>>): Shareable | undefined {

  const keys = ['systemSharing', 'shared', 'sensitive'] as const;
  if (!keys.some(key => record[key] !== undefined && record[key] !== null)) return undefined;
  return { systemSharing: record['systemSharing'] as boolean | undefined, shared: record['shared'] as ActivityShareType[] | undefined, sensitive: record['sensitive'] as boolean | undefined };
}
